import { useUser } from '@myosh/myosh-login';
import { useEffect, useMemo, useState } from 'react';
import type { CurrentUserResult, OdinUser } from '../@types/users';
import { useGetCurrentUserQuery } from '../redux/services/user';
import { UserFields } from '../utils/user-config';
import { hasAdminAccess, transformUserHierarchies } from '../utils/user-functions';

type ProfileData = {
  user?: OdinUser;
};

function userResponseToProfileUser(userResponse: CurrentUserResult) {
  return {
    id: userResponse.superUser ? userResponse.userId : userResponse.id,
    firstName: userResponse.superUser ? userResponse.userId : userResponse.firstName,
    lastName: userResponse.superUser ? '' : userResponse.lastName,
    hasAdminAccess: hasAdminAccess(userResponse),
    groups: userResponse.groups ?? [],
    adminAccess: userResponse.adminAccess ?? [],
    superUser: Boolean(userResponse.superUser),
    hierarchyAccessRestrictions: userResponse.userHierarchyAccessRestriction ?? [],
    isExternal: userResponse.externalUser,
    userHierarchies: transformUserHierarchies(userResponse.userHierarchy),
    fullName: userResponse.fullName,
    passwordChangeRequired: userResponse.passwordChangeRequired,
    ssoUser: userResponse.ssoUser,
    email: userResponse.email,
  } as OdinUser;
}

const useProfileData = () => {
  const [profileData, setProfileData] = useState<ProfileData>({});
  const { state: userState } = useUser();

  // this hook needs to use `isLoading`
  const {
    data,
    isLoading: isLoadingUser,
    isFetching: isFetchingUser,
  } = useGetCurrentUserQuery(
    {
      fields: [
        UserFields.adminAccess,
        UserFields.externalUser,
        UserFields.groups,
        UserFields.superuser,
        UserFields.userHierarchyAccess,
        UserFields.userHierarchy,
        UserFields.ssoUser,
      ],
    },
    { skip: userState === undefined, refetchOnMountOrArgChange: 300 /* 5 minutes */ }
  );

  const userData = useMemo(() => {
    return data ? userResponseToProfileUser(data) : undefined;
  }, [data]);

  useEffect(() => {
    if (!isLoadingUser) {
      const superUser = userState?.user?.superuser === 'true';
      const user = userData ?? (superUser ? defaultSuperUser : undefined);

      setProfileData((prevState) => {
        return {
          ...prevState,
          user,
        };
      });
    }
  }, [userData, isLoadingUser]);

  return {
    profileData,
    isLoadingUser,
    isFetchingUser,
  };
};

export default useProfileData;

const defaultSuperUser: OdinUser = {
  id: -1, // virtual user
  firstName: 'Super',
  lastName: 'User',
  hasAdminAccess: true,
  passwordChangeRequired: false,
};
